/**
 * Voucher — one operator-named code carrying two independent halves: a
 * `discount` that takes money off, and `unlocks` that reveals ticket types the
 * public shop hides. Either half may be absent, and the common code carries
 * both — press need a revealed free ticket, partners a revealed cheaper tier,
 * a newsletter a plain 10 % off. A code with neither half takes nothing off
 * and reveals nothing, so the schema's own refine refuses it: a union over the
 * two halves would refuse it too, at the price of making the both-halves case
 * two objects.
 *
 * Path: `workspaces/{orgId}/events/{eventId}/vouchers/{code}` — a code belongs
 * to exactly one event, and containment is where that is stated: nothing in
 * the document re-declares it. A code an operator wants on three events is
 * three documents, and uniqueness is per event, so the same `PRESSE25` may
 * exist on two of them and means whatever each event's own document says. The
 * doc id is the code, typed by the operator so it can be printed, spoken and
 * put in a newsletter. A guessed code is therefore contained, never prevented:
 * `usageLimit`, `constraints`, `perBuyerLimit` and VOUCHER_ATTEMPT_CAP per
 * checkout bound what walking the obvious guesses is worth, and the code's own
 * secrecy bounds nothing.
 *
 * `usedCount` is consumed inside the settle transaction — quota-like state
 * with the same single writer.
 *
 * Policy: read and CRUD alike gate on `tickets.vouchers.manage` + `mfa: true`
 * — one authority for the whole operator surface, since a member who can see
 * a code's remaining redemptions can already mint a code of their own. No
 * buyer-facing rule exists at any tier, so an anonymous or ordinary
 * signed-in client falls to the generated default-deny tail: operator-named
 * codes are short and guessable, and a publicly readable document is a free
 * enumeration surface that also discloses how many redemptions each code has
 * left. The buyer never needs one — every resolution happens server-side
 * against the Admin SDK, which rules do not gate. The read predicate is
 * `hasPermission` + `mfa` and touches no `resource.data`, so it emits as a
 * combined `allow read:` covering the operator screens' `get` AND their
 * `list` over the collection.
 *
 * `usedCount` carries no write mask keeping members out of it. The mask would
 * defend nothing: the same token already allows `delete`, and deleting a code
 * then re-minting it at the same id reaches every state a reset counter does.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import {
	apportionByRate,
	type DiscountLine,
	type TicketLine,
} from "./order.entity.ts";
import {
	PositiveCents,
	resolveClaimableBy,
	type TicketType,
} from "./ticket-type.entity.ts";
import type { Actor } from "./workspace.entity.ts";

/**
 * The most refusals one checkout may collect before its code field stops
 * being answered at all — the per-checkout price of walking the obvious
 * guesses, high enough that a legitimate buyer's typos never reach it.
 *
 * FLAGGED: product policy with no system-boundary provenance — no Firestore,
 * PSP or rules limit sets this number. The containment that does have
 * provenance is `usageLimit` and `constraints`.
 */
export const VOUCHER_ATTEMPT_CAP = 10;

// Only `value`'s validation differs between the branches (a percentage 1–100
// vs cents that must actually move), so the two carry nothing else in common
// to declare once. 100 is a legal value and not a comp route: resolveVoucher
// refuses the basket it would take to zero, and the operator's path to a free
// ticket is the unlock half over a €0 type.
const VoucherDiscountSchema = z.discriminatedUnion("type", [
	z.object({
		type: z.literal("percent"),
		value: z
			.number()
			.int()
			.min(1)
			.max(100)
			.describe("percentage off the eligible lines"),
	}),
	z.object({
		type: z.literal("fixed"),
		value: PositiveCents.describe("cents off the eligible lines"),
	}),
]);

// events/{eventId}/vouchers/{code} — member read + CRUD, no buyer face
// Fields justified:
//   label            — what this code is for, read by the staff list and the
//                      form; the doc id names the code, never its purpose
//   discount         — the money half, absent on a code that only unlocks
//   unlocks          — the visibility half, absent on a code that only discounts
//   constraints      — where the discount may land, and until when
//   usageLimit       — total redemptions across everyone
//   perBuyerLimit    — redemptions by one person, counted on the redemption doc
//   usedCount        — settle's own counter
//   createdAt/By     — who gave money away, and when
// Excluded:
//   code — the doc id already carries it, and a stored copy is free to
//     contradict the path it is read at. resolveVoucher below takes the code
//     joined onto the parsed document from `snap.id`, so the one
//     representation stays the path
//   any priced or discounted total — every figure is re-derived from
//     (voucher, basket, catalog) by resolveVoucher below until settle stamps
//     the order, which is the one place a discount becomes a stored fact
//   an active/enabled flag — validUntil, usageLimit and usedCount already
//     answer whether this code still applies; a third switch is free to
//     disagree with them
//   the buyers who redeemed it — the redemptions subcollection is that record,
//     keyed by a hash rather than an email
//   a partner id, and the partner record it would point at — nothing is owed
//     to whoever is credited, so there is no balance, no payout and no contact
//     to keep; the credit is a label on the code, and an entity would be a
//     second place for a name that has no other fact to carry
//   the event this code applies to — the document sits under the event that
//     owns it, so the path is that answer; a field beside it would be a second
//     one, free to disagree with the path it was read at
export const VoucherSchema = z
	.object({
		label: z
			.string()
			.min(1)
			.describe(
				"what this code is for, in the operator's own words — staff-facing, never rendered to a buyer",
			),
		discount: VoucherDiscountSchema.nullish().describe(
			"what this code takes off the eligible lines — a percentage of their subtotal, or a fixed number of cents clamped to it. Absent on a code that only reveals hidden ticket types",
		),
		unlocks: z
			.array(z.string())
			.min(1)
			.nullish()
			.describe(
				"the ticket types this code reveals — a type whose claimableBy resolves 'code' is hidden from the public shop and buyable only in a checkout carrying a code that names it. Absent on a code that only takes money off",
			),
		constraints: z
			.object({
				typeIds: z.array(z.string()).min(1).nullish(),
				// The zone the operator authored this instant in is the venue's,
				// resolved the way every other wall-clock field on this event
				// already resolves it — resolveEffectiveTimezone in
				// ticket-type.entity.ts. Stated on the field because nothing in a
				// Timestamp carries it, and a consumer left to pick would pick the
				// browser's, expiring a code an hour early for anyone travelling.
				// resolveVoucher compares instants, which have no zone, so this
				// binds what the form writes and what a screen renders, not the
				// comparison.
				validUntil: z
					.custom<Timestamp>()
					.nullish()
					.describe(
						"the instant this code stops applying, authored in the timezone of the venue holding the event that owns it — the only clock the operator setting an expiry is reading",
					),
			})
			.default({})
			.describe("absent constraint means unconstrained"),
		usageLimit: z
			.number()
			.int()
			.min(1)
			.describe("total redemptions this code allows across everyone"),
		perBuyerLimit: z
			.number()
			.int()
			.min(1)
			.nullish()
			.describe(
				"the most redemptions one person may make on this code, counted on that person's own redemption document. Absent is unlimited per person — the code is then bounded by usageLimit alone",
			),
		usedCount: z
			.number()
			.int()
			.min(0)
			.default(0)
			.describe(
				"consumed inside the settle transaction — quota-like state with the same single writer, settleOrder",
			),
		createdAt: z.custom<Timestamp>().describe("when this code was issued"),
		createdBy: z
			.string()
			.describe("the uid of the member who issued this code"),
	})
	.refine((code) => code.discount != null || code.unlocks != null, {
		message: "a voucher takes money off, reveals a ticket type, or both",
	});
export type Voucher = z.infer<typeof VoucherSchema>;
// A writer that omits a .default()-bearing field (constraints, usedCount) and
// relies on VoucherSchema.parse to fill it types its own literal against this
// z.input, not z.infer's output.
export type VoucherInput = z.input<typeof VoucherSchema>;

// events/{eventId}/vouchers/{code}/redemptions/{buyerKey} — one document
// per person per code.
// The doc id is the SHA-256 of the buyer's lowercased email: the cap needs an
// identity, and the order already holds the email, so a second home for it
// here would earn nothing.
// Fields justified:
//   count           — what perBuyerLimit is checked against
//   lastOrderId     — the join staff read a name and an email through
//   lastRedeemedAt  — when this person last used the code
// Excluded:
//   email / name — the doc id is a hash by design; the join through
//     lastOrderId is what renders a person, one read per row
//   every order id that redeemed the code — each order carries the code it
//     used, so the list is answerable from the orders side; count and the last
//     one are what the cap and the code's own screen read
export const VoucherRedemptionSchema = z.object({
	count: z
		.number()
		.int()
		.min(1)
		.describe(
			"redemptions of this code by this person — written inside the settle transaction with the code's own usedCount, by the same single writer",
		),
	lastOrderId: z
		.string()
		.describe("the most recent order that redeemed this code for this person"),
	lastRedeemedAt: z.custom<Timestamp>(),
});
export type VoucherRedemption = z.infer<typeof VoucherRedemptionSchema>;

/**
 * The one validity decision and the one arithmetic a code produces — pure, and
 * the single authority the storefront's displayed basket, preparePayment's
 * authoritative re-price and settle's own re-resolution all read, so no two of
 * them can price one code differently. Decision and lines are one answer, not
 * a decide/apply pair: the lines ARE what applying the code means.
 *
 * Refused — one indistinguishable shape, because a refusal that named its
 * reason would turn the code field into an oracle over which codes exist, when
 * they expire and how many redemptions each has left:
 *   · the code resolves to no document
 *   · this checkout has already been refused VOUCHER_ATTEMPT_CAP times
 *   · `now` is past `constraints.validUntil`
 *   · `usedCount` has reached `usageLimit`
 *   · this person has reached `perBuyerLimit`
 *   · the basket holds a line whose type resolves `claimableBy: 'code'` that
 *     this code does not unlock, or one whose type the catalog cannot resolve
 *   · the code carries no half at all — nothing to take off and nothing to
 *     reveal
 *   · a code that only discounts takes nothing off: nothing in the basket is
 *     eligible, the eligible lines are worth nothing, or the discount would
 *     consume the whole of what it applies to
 *
 * Applied — the discount lines, one per distinct VAT rate among the eligible
 * lines. Each rate takes the floor of its share of the eligible subtotal, and
 * the cents left over go one apiece to the largest fractional remainders,
 * ties to the lower rate, so the parts sum back to the discount exactly.
 * § 10 Abs. 1 UStG makes a Rabatt reduce the Entgelt of the supplies it
 * relates to, which is why a mixed 7 %/19 % basket splits rather than folding
 * down to one figure. Only ticket lines are ever eligible.
 *
 * `eligibleTypeIds` names the distinct types of those same eligible lines —
 * which supplies the discount reduced, a fact the discount lines themselves
 * cannot carry, since each names a rate and no type. It is empty wherever the
 * money half does not land: the code applied and took nothing off.
 *
 * Scope by event is the path's, never a parameter's: the caller reads the
 * document from the checkout's own event, so a code belonging to another event
 * resolves to no document and takes the absent-code branch below.
 *
 * The code travels joined onto its own document, as the read seam already
 * holds it — `{ ...VoucherSchema.parse(snap.data()), code: snap.id }` — so it
 * has one representation the whole way to the `DiscountLine.code` stamped on
 * the order.
 *
 * The unlock never depends on the discount landing: a code that reveals a type
 * applies with no discount lines wherever the money half does not land. The
 * type it reveals is hidden until this answer says otherwise, so a refusal
 * there is a closed loop — the buyer cannot add a line for a type they cannot
 * see, and a discount scoped to that type can then never have anything to
 * apply to. Every payload change re-resolves, so the discount lands on the
 * pass after the buyer adds the revealed type.
 *
 * `buyerRedemptionCount` is null while the identity is unknown — the buyer's
 * contact has not validated yet — and the per-person cap is then unchecked
 * here and enforced at settle, where an identity always exists.
 */
export function resolveVoucher(input: {
	voucher: (Voucher & { code: string }) | undefined;
	ticketLines: readonly Pick<TicketLine, "typeId" | "vatRate" | "amount">[];
	types: ReadonlyMap<string, Pick<TicketType, "price" | "claimableBy">>;
	buyerRedemptionCount: number | null;
	// The instant to measure `validUntil` against, as the millis it carries and
	// nothing more. Every stored field above is typed against the client SDK's
	// Timestamp, while both callers that price a real basket hold the Admin
	// SDK's — two structurally distinct classes for one runtime value — and a
	// comparison of instants needs neither of them whole.
	now: Pick<Timestamp, "toMillis">;
	attempts: number;
}):
	| {
			status: "applied";
			discountLines: DiscountLine[];
			unlockedTypeIds: string[];
			eligibleTypeIds: string[];
	  }
	| { status: "refused" } {
	const refused = { status: "refused" } as const;
	const voucher = input.voucher;
	if (voucher === undefined) return refused;
	if (input.attempts >= VOUCHER_ATTEMPT_CAP) return refused;

	const validUntil = voucher.constraints.validUntil;
	if (validUntil != null && input.now.toMillis() > validUntil.toMillis())
		return refused;
	if (voucher.usedCount >= voucher.usageLimit) return refused;
	if (
		voucher.perBuyerLimit != null &&
		input.buyerRedemptionCount != null &&
		input.buyerRedemptionCount >= voucher.perBuyerLimit
	)
		return refused;

	// The audience gate settle enforces per line, read here so the same basket
	// cannot price one way and settle another: a hidden type in the basket is
	// buyable only under a code that names it, and a type this catalog cannot
	// resolve is one no audience answer exists for.
	const unlockedTypeIds = voucher.unlocks ?? [];
	for (const line of input.ticketLines) {
		const type = input.types.get(line.typeId);
		if (type === undefined) return refused;
		if (
			resolveClaimableBy(type) === "code" &&
			!unlockedTypeIds.includes(line.typeId)
		)
			return refused;
	}

	// The answer wherever the money half does not land — see the unlock note
	// above. A code with nothing to reveal has nothing left to apply, so it
	// refuses; one that reveals a type applies on that alone.
	const withoutDiscount =
		unlockedTypeIds.length > 0
			? {
					status: "applied" as const,
					discountLines: [],
					unlockedTypeIds,
					eligibleTypeIds: [],
				}
			: refused;
	if (voucher.discount == null) return withoutDiscount;

	const typeIds = voucher.constraints.typeIds;
	const eligible = input.ticketLines.filter(
		(line) => typeIds == null || typeIds.includes(line.typeId),
	);
	const eligibleSubtotal = eligible.reduce((sum, line) => sum + line.amount, 0);
	// Rounded to the nearest cent, the same direction splitGross rounds the
	// figure it derives. The clamp on a fixed discount is the only expressible
	// answer to one larger than what it applies to.
	const discount =
		voucher.discount.type === "percent"
			? Math.round((eligibleSubtotal * voucher.discount.value) / 100)
			: Math.min(voucher.discount.value, eligibleSubtotal);
	// Zero covers both the basket nothing in scope reaches and the scope whose
	// lines are worth nothing to begin with.
	if (discount <= 0) return withoutDiscount;
	// A discount may not consume the whole of the supplies it reduces: tickets
	// given away entirely are a comp, and the comp idiom is a €0 type revealed
	// by the unlock half, which invoices as a €0 supply rather than a
	// discounted one. The sum is the ticket lines because those are the
	// supplies a discount reaches — a line kind it cannot reduce neither
	// enters this sum nor is protected by it, and since such a line only ever
	// adds to the authoritative gross, a ticket subtotal that survives its own
	// discount is what keeps that gross off zero, where 69's free arm (a
	// document whose every line is priced at 0) cannot carry the order and
	// preparePayment abandons the mint. It takes the same branch as any other
	// non-landing discount rather than a refusal of its own, because a code
	// reveals a type or it does not — an arithmetic outcome retracting a
	// visibility grant is what strands a revealed line in a basket nothing can
	// settle.
	const ticketGross = input.ticketLines.reduce(
		(sum, line) => sum + line.amount,
		0,
	);
	if (ticketGross - discount <= 0) return withoutDiscount;

	// One apportionment rule, shared with the booking fee's own lines
	// (order.entity.ts's apportionByRate): the discount is handed over
	// positive and the sign flipped here, because the rounding may not depend
	// on the direction or the two stop agreeing on a mixed basket.
	const discountLines: DiscountLine[] = apportionByRate(discount, eligible).map(
		({ vatRate, cents }) => ({
			kind: "discount",
			code: voucher.code,
			vatRate,
			amount: -cents,
		}),
	);
	return {
		status: "applied",
		discountLines,
		unlockedTypeIds,
		eligibleTypeIds: [...new Set(eligible.map((line) => line.typeId))],
	};
}

export const Voucher = entity<Actor>()({
	schema: VoucherSchema,
	actions: ["read", "create", "update", "delete"] as const,
})
	.pipe(({ when, hasPermission, and, pathVar }) => [
		// One clause for the whole operator surface — see the file-level note
		// on why the read face carries the same authority as the write face,
		// and why no buyer-facing clause joins it.
		when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.vouchers.manage", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		).allow("read", "create", "update", "delete"),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/events/{eventId}/vouchers/{code}",
		}),
	);

export const VoucherRedemption = entity<Actor>()({
	schema: VoucherRedemptionSchema,
	actions: ["read"] as const,
})
	.pipe(({ can, hasPermission, and, pathVar }) => [
		// The redemption list a code's own screen renders, joined per row to
		// its lastOrderId for a name and an email. Member-only under the same
		// token, and client-unwritable at every tier: settleOrder writes these
		// documents inside its own transaction, with the code's usedCount.
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.vouchers.manage", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/events/{eventId}/vouchers/{code}/redemptions/{buyerKey}",
		}),
	);
